'use client';

/** Presentational entity cards — chips for lists, a receipt for mutations. */

import React from 'react';
import { ExternalLinkIcon } from 'lucide-react';
import { ENTITY_NOUNS, ENTITY_ROUTES, type EntityKind } from './entity-kinds.js';
import { MAX_CHIPS, type EntityView } from './entity-view.js';

const CHIP_CLASS =
  'inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-muted/60 px-2.5 py-0.5 text-xs';

function entityHref(kind: EntityKind, id: string): string {
  return `${ENTITY_ROUTES[kind]}/${encodeURIComponent(id)}`;
}

export function EntityChip({ entity, kind }: { entity: EntityView; kind: EntityKind }) {
  const body = (
    <>
      <span className="font-medium truncate">{entity.primary}</span>
      {entity.secondary && (
        <span className="text-muted-foreground truncate">{entity.secondary}</span>
      )}
    </>
  );
  if (!entity.id) {
    return <span className={CHIP_CLASS}>{body}</span>;
  }
  return (
    <a
      href={entityHref(kind, entity.id)}
      target="_blank"
      rel="noreferrer"
      className={`${CHIP_CLASS} hover:border-primary/50 hover:bg-primary/5 transition-colors`}
    >
      {body}
    </a>
  );
}

export function EntityChipList({ views, kind }: { views: EntityView[]; kind: EntityKind }) {
  const shown = views.slice(0, MAX_CHIPS);
  const hidden = views.length - shown.length;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {shown.map((v, i) => (
        <EntityChip key={v.id ?? `${v.primary}-${i}`} entity={v} kind={kind} />
      ))}
      {hidden > 0 && <span className="text-xs text-muted-foreground">+{hidden} more</span>}
    </div>
  );
}

/** Receipt for a mutation: what was created/updated, with a link into Hub. */
export function MutationReceipt({ view, kind }: { view: EntityView; kind: EntityKind }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs">
      <span className="min-w-0 truncate">
        <span className="capitalize">{ENTITY_NOUNS[kind]}</span>{' '}
        <span className="font-medium">{view.primary}</span>
        {view.secondary && <span className="text-muted-foreground"> · {view.secondary}</span>}
      </span>
      {view.id && (
        <a
          href={entityHref(kind, view.id)}
          target="_blank"
          rel="noreferrer"
          className="ml-auto inline-flex flex-shrink-0 items-center gap-1 font-medium text-primary hover:underline"
        >
          Open <ExternalLinkIcon className="size-3" />
        </a>
      )}
    </div>
  );
}
